import React, { useState } from "react";
import { ReactComponent as RefuseErrorImage } from "../../../../assets/icons/svg/refused_error_image.svg";
import { ReactComponent as ConnectionErrorIcon  } from "../../../../assets/icons/svg/ErrorPage/connection-error.svg";
import { ReactComponent as ShieldIcon  } from "../../../../assets/icons/svg/ErrorPage/shield.svg";
import Button from "../../../ui/buttons";
import GrantAccessDrawer from "./GrantAccessDrawer";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";

interface Props {
  errorType: "network" | "blocked" | "timeout" | null;
  url: string;
  onRetry: () => void;
}

const font = { fontFamily: DEFAULT_FONT_FAMILY };


export default function IframeErrorPage({ errorType, url, onRetry }: Props) {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showWhyInfo, setShowWhyInfo] = useState(false);

  const hostname = (() => {
    try { return new URL(url).hostname; }
    catch { return url; }
  })();

  const handleReload = () => {
    setShowDrawer(false);
    onRetry();
  };

  if (errorType === "network") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center w-full h-full bg-white rounded-t-[8px] px-6" style={font}>
        <RefuseErrorImage className="w-20 h-20 mb-3" />
        <p className="text-base 3xl:text-[18px] font-semibold text-[#2A3547] mb-1">
          Dev Server Not Running
        </p>
        <p className="text-sm 3xl:text-[16px] font-normal text-[#2A354799] text-center max-w-lg mb-1">
          Could not reach{" "}
          <span className="font-semibold text-[#2A3547]">{hostname}</span>.
          Make sure your development server is running.
        </p>
        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-left w-full max-w-lg">
          <p className="text-xs 3xl:text-[14px] font-medium text-[#2A354799] mb-2 uppercase tracking-wide">
            Try one of these
          </p>
          <code className="block text-xs 3xl:text-[14px] text-[#2A3547] mb-1">npm run dev</code>
          <code className="block text-xs 3xl:text-[14px] text-[#2A3547] mb-1">yarn dev</code>
          <code className="block text-xs 3xl:text-[14px] text-[#2A3547]">pnpm dev</code>
        </div>
        <Button variant="primary" size="sm" onClick={onRetry} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  if (errorType === "timeout") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center w-full h-full bg-white rounded-t-[8px] px-6" style={font}>
        <RefuseErrorImage className="w-20 h-20 mb-3" />
        <p className="text-base 3xl:text-[18px] font-semibold text-[#2A3547] mb-1">
          Preview Timed Out
        </p>
        <p className="text-sm 3xl:text-[16px] font-normal text-[#2A354799] text-center max-w-lg">
          <span className="font-semibold text-[#2A3547]">{hostname}</span> took
          too long to respond. Check that the server is running and the URL is correct.
        </p>
        <Button variant="primary" size="sm" onClick={onRetry} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  /* Blocked / refused to connect */
  return (
    <div className="relative flex-1 w-full overflow-hidden bg-white rounded-t-[8px]" style={font}>
      <div className="flex flex-col items-center justify-center w-full h-full px-6 py-6">
        <ConnectionErrorIcon className="w-18 h-18" />

        <p className="mt-4 text-[24px] 3xl:text-[26px] font-medium text-[#192028] text-center">
          Oops! Couldn't connect
        </p>
        <p className="mt-1.5 text-sm 3xl:text-[16px] font-normal text-[#5A6572] text-center max-w-lg">
          <span className="font-semibold text-[#2A3547]">{hostname}</span>{" "}
          turned Cluva away at the door and that's on your site, not us
        </p>

        {/* Card */}
        <div className="mt-5 w-full max-w-2xl bg-[#FBFDFF] border border-[#A3D8FF] rounded-[10px] py-10 flex flex-col items-center">
          <div className="w-full max-w-md flex flex-col items-center">
            <ShieldIcon className="w-14 h-14" />
            <p className="mt-3 text-[18px] 3xl:text-[20px] font-medium text-[#192028] text-center">
              Nice! that block was on purpose
            </p>
            <p className="mt-2 text-xs 3xl:text-[14px] font-normal text-[#5A6572] text-center leading-relaxed max-w-sm">
              Your site refuses to load inside anything it doesn't recognize,
              which is exactly what a well-secured site should do. Add cluva to
              its guest list and everyone else stays locked out.
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowDrawer(true)}
              className="mt-4 px-6 gap-2"
            >
              Allow Cluva access →
            </Button>
            <p className="mt-2 text-[12px] 3xl:text-[14px] font-normal text-[#192028]">
              Takes about a minute
            </p>
          </div>
        </div>

        {/* Why this is needed */}
        <button
          onClick={() => setShowWhyInfo(true)}
          className="mt-4 text-[12px] 3xl:text-[14px] font-normal hover:underline"
          style={{ color: "#2A354799" }}
        >
          Why this is needed?
        </button>
      </div>

      {/* Why this is needed — modal */}
      {showWhyInfo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[56] flex items-center justify-center"
          onClick={() => setShowWhyInfo(false)}
        >
          <div
            className="bg-white rounded-[10px] shadow-xl w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg overflow-hidden mx-4"
            style={font}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 pt-4 pb-2">
              <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#1E1F26]">
                Why this is needed?
              </h2>
            </div>

            <hr className="border-gray-200 mx-4" />

            {/* Body */}
            <div className="px-4 py-5 space-y-4">
              <div className="flex items-start gap-3">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="8" fill="#53AEF4" fillOpacity="0.15" />
                  <path d="M5 8l2 2 4-4" stroke="#53AEF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <p className="text-sm 3xl:text-[16px] font-semibold text-[#1E1F26]">Read-only.</p>
                  <p className="text-sm 3xl:text-[16px] font-normal text-[#2A354799] mt-0.5">
                    OpenUI only displays your live site, it never edits or publishes anything.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="8" fill="#53AEF4" fillOpacity="0.15" />
                  <path d="M5 8l2 2 4-4" stroke="#53AEF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <p className="text-sm 3xl:text-[16px] font-semibold text-[#1E1F26]">You stay in control.</p>
                  <p className="text-sm 3xl:text-[16px] font-normal text-[#2A354799] mt-0.5">
                    Add or remove OpenUI as a trusted source anytime.
                  </p>
                </div>
              </div>
            </div>

            <hr className="border-gray-200 mx-4" />

            {/* Footer */}
            <div className="px-4 py-4 flex justify-end">
              <Button variant="cancel" size="lg" onClick={() => setShowWhyInfo(false)} className="text-sm">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDrawer && (
        <div className="fixed inset-0 bg-black/20 z-[55]" onClick={() => setShowDrawer(false)} />
      )}
      {showDrawer && (
        <GrantAccessDrawer onClose={() => setShowDrawer(false)} onReload={handleReload} />
      )}
    </div>
  );
}
